import React, { useEffect, useState, useRef } from "react";
import FormField from "./FormField";
import DateRangePicker from "./DateRangePicker";
import { Filter, X } from "lucide-react";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

export interface ICommonOption {
    value: string | number;
    label: string;
}

export interface IFilter {
    filter?: string;
    sort?: string;
    [key: string]: any;
}

interface CommonFilterProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    value?: IFilter;
    filterOptions?: ICommonOption[];
    sortOptions?: ICommonOption[];
    onChange?: (value: string, name: string) => void;
    searchPlaceholder?: string;
    filterPlaceholder?: string;
    sortPlaceholder?: string;
    filterName?: string;
    sortName?: string;
    startDate?: string;
    endDate?: string;
    onDateRangeChange?: (startDate: string, endDate: string) => void;
    singleDate?: string;
    onSingleDateChange?: (date: string) => void;
    additionalFilters?: React.ReactNode;
    createSalaryFilter?: boolean;
}

const CommonFilter: React.FC<CommonFilterProps> = ({
    searchValue = "",
    onSearchChange,
    value,
    filterOptions = [],
    sortOptions = [],
    onChange,
    searchPlaceholder = "Search...",
    filterPlaceholder = "Filter",
    sortPlaceholder = "Sort By",
    filterName = "filter",
    sortName = "sort",
    startDate = "",
    endDate = "",
    onDateRangeChange,
    singleDate = "",
    onSingleDateChange,
    additionalFilters,
    createSalaryFilter,
}) => {
    const [search, setSearch] = useState(searchValue);
    const [filterOpen, setFilterOpen] = useState(false);

    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => {
        onSearchChangeRef.current = onSearchChange;
    }, [onSearchChange]);

    useEffect(() => {
        setSearch(searchValue || "");
    }, [searchValue]);

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            const sanitizedSearch = search ? search.replace(/\s+/g, "").trim().toLowerCase() : "";
            onSearchChangeRef.current?.(sanitizedSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    return (
        <div className="flex flex-col sm:flex-row gap-4 justify-between flex-wrap items-stretch sm:items-center mb-5">
            {/* Search */}
            {onSearchChange !== undefined ? (
                <div className="relative flex-1 max-w-sm min-w-60">
                    <FormField
                        type="search"
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            ) : (
                <div></div>
            )}

            {/* Filter & Sort */}
            <div className="flex justify-start sm:justify-end gap-2 flex-wrap">
                <div className="flex flex-wrap gap-4 w-full sm:w-auto sm:justify-end items-start">
                    {additionalFilters}
                    {/* Single Date Picker */}
                    {onSingleDateChange !== undefined && (
                        <div className="flex items-center gap-2 relative z-30 w-full sm:w-auto">
                            <div className="relative w-full sm:w-44">
                                <input
                                    type="date"
                                    value={singleDate}
                                    onChange={(e) => onSingleDateChange(e.target.value)}
                                    className="w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                                />
                            </div>
                            {singleDate && (
                                <Button
                                    variant="outline"
                                    size="icon-lg"
                                    className="bg-white "
                                    type="button"
                                    onClick={() => onSingleDateChange("")}
                                    title="Clear Date"
                                >
                                    <X size={16} />
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Date Range Picker */}
                    {onDateRangeChange !== undefined && (
                        <div className="flex items-center gap-2 relative z-30 w-full sm:w-auto">
                            <div className="">
                                <DateRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={onDateRangeChange}
                                    placeholder="Filter by Date Range"
                                />
                            </div>
                            {(startDate || endDate) && (
                                <Button
                                    variant="outline"
                                    size="icon-lg"
                                    className="bg-white"
                                    type="button"
                                    onClick={() => onDateRangeChange("", "")}
                                    title="Clear Date Range"
                                >
                                    <X size={16} />
                                </Button>
                            )}
                        </div>
                    )}
                    {filterOptions?.length > 0 && (
                        <FormField
                            type="select"
                            name={filterName}
                            placeholder={filterPlaceholder}
                            value={value?.[filterName] || ""}
                            onChange={(e) => onChange?.(e.target.value, filterName)}
                            options={filterOptions?.map(opt => ({
                                label: opt.label,
                                value: String(opt.value)
                            }))}
                        />
                    )}

                    {sortOptions?.length > 0 && (
                        <FormField
                            type="select"
                            name={sortName}
                            placeholder={sortPlaceholder}
                            value={value?.[sortName] || ""}
                            onChange={(e) => onChange?.(e.target.value, sortName)}
                            options={sortOptions?.map(opt => ({
                                label: opt.label,
                                value: String(opt.value)
                            }))}
                        />
                    )}
                </div>
                {/* filter modal */}
                {/* createSalaryFilter={true} */}
                {createSalaryFilter &&
                    <Button
                        type="button"
                        variant="outline"
                        className="bg-white"
                        onClick={() => setFilterOpen(true)}
                        title="Open Filters"
                    >
                        <Filter size={14} />
                    </Button>
                }
            </div>

            {/* Filter Dialog */}
            {createSalaryFilter &&
                <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Filter size={18} />
                                Filters
                            </DialogTitle>
                        </DialogHeader>

                        <div className="flex flex-col gap-4 py-2">
                            {/* Status */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <select className="w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary">
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>

                            {/* Department */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700">Department</label>
                                <select className="w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary">
                                    <option value="">All Departments</option>
                                    <option value="hr">HR</option>
                                    <option value="finance">Finance</option>
                                    <option value="engineering">Engineering</option>
                                    <option value="sales">Sales</option>
                                </select>
                            </div>

                            {/* Date Range */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700">Date Range</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="date"
                                        className="flex-1 h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                                        placeholder="From"
                                    />
                                    <span className="text-gray-400 text-sm">to</span>
                                    <input
                                        type="date"
                                        className="flex-1 h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                                        placeholder="To"
                                    />
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setFilterOpen(false)}
                            >
                                Reset
                            </Button>
                            <Button
                                type="button"
                                className="flex-1"
                                onClick={() => setFilterOpen(false)}
                            >
                                Apply Filters
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            }
        </div>
    );
};

export default CommonFilter;